import Vue from 'vue'
import Vuex from 'vuex'
import lodash from 'lodash'
import router from './config/router'
import cookie from 'js-cookie'
import moment from 'moment'
import i18n from './i18n'

Vue.use(Vuex)

let generateUuidv4 = function() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  })
}

const state = {
  header: {
    compressed: false
  },
  footer: {
    display: true
  },
  current_user: null,
  currentProject: null,
  organization_config: {},
  current_organization_plan_active: true,
  page_title: "",
  page_breadcrumbs: [],
  parent_page_title: { name: '', link: {} },
  page_type: "",
  error: "",
  page_level: '2',
  folded_navigation_width: 0,
  folded_navigation_hidden_items: [],
  drawer_visible: false,
  header_buttons: [],
  notifications: [],
  maxNotificationsCount: 5,
  tabs: [],
  active_tab: '',
  currentPage: {
    projects: 1,
    segments: 1,
    leads: 1,
    users: 1,
    externalForms: 1,
    organizations: 1,
    leadMasks: 1,
    emailTemplates: 1,
    workflows: 1
  },
  dragEditor: {
    unpublishedChanges: 0,
    edited: false,
    activeItem: {},
    activeTab: {},
    currentStructureObject: {},
    currentStructure: [],
    submitButton: null
  },
  lead: {},
  intercomInitialized: false,
  emptyState: null,
  isRetrieving: false,
  projectWizard: {
    customBusinessKit: false,
    step: 1,
    errors: [],
  },
  AppDownloadScreen: false,
  notAllowedModalShown: false, // User clicked a menu item they are not allowed to use
  mainContainerClasses: [], // extra classes for main container. Can be built dynamically
  secondaryHeaderTabs: [],
  language: i18n.locale
}

const mutations = {
  SET_FOOTER_DISPLAY: (state, payload) => {
    state.footer.display = payload
  },
  SET_HEADER_COMPRESSED: (state, payload) => {
    state.header.compressed = payload
  },
  RESET_DRAG_EDITOR: (state) => {
    state.dragEditor.unpublishedChanges = 0
    state.dragEditor.activeTab = {}
  },
  SET_DRAG_EDITOR_ACTIVE_TAB: (state, payload) => {
    state.dragEditor.activeTab = payload
  },
  SET_DRAG_EDITOR_SUBMIT_BUTTON: (state, payload) => {
    state.dragEditor.submitButton = payload
  },
  SET_DRAG_EDITOR_CURRENT_STRUCTURE: (state, payload) => {
    state.dragEditor.currentStructure = payload
  },
  SET_DRAG_EDITOR_EDITED: (state, payload) => {
    state.dragEditor.edited = payload
  },
  SET_DRAG_EDITOR_UNPUBLISHED_CHANGES: (state, payload) => {
    state.dragEditor.unpublishedChanges = payload
  },
  SET_DRAG_EDITOR_ACTIVE: (state, payload) => {
    if (payload && payload.collection) {
      delete payload.collection
      delete payload.collectionIdx
    }
    if (payload) {
      state.dragEditor.activeItem = payload
    }
  },
  UPDATE_DRAG_EDITOR_ACTIVE: (state, payload) => {
    Vue.set(state.dragEditor.activeItem, payload.attribute, payload.value)
    state.dragEditor.edited = true
  },
  CHANGE_LANGUAGE: (state, language) => {
    i18n.locale = language
    moment.locale(language)
    state.language = language
    cookie.set('language',language)
  },
  EMPTY_LEAD: (state) => {
    state.lead = {}
  },
  UPDATE_CURRENT_LEAD: (state, params) => {
    // Overwrite parameters from lead if conflict
    let update_lead = _.extend({}, state.lead, params);
    state.lead = update_lead
  },
  SET_SECONDARY_HEADER_TABS: (state, tabs) => {
    // Setting secondaryHeaderTabs adds an array of objects as tabs to
    // the secondNavigation component
    state.secondaryHeaderTabs = tabs
  },
  SET_CURRENT_ORG_CONFIG: (state, title) => {
    state.organization_config = title
  },
  SET_MAIN_CONTAINER_CLASSES: (state, payload) => {
    state.mainContainerClasses = payload
  },
  SET_CURRENT_ORGANIZATION_PLAN_ACTIVE: (state, bool) => {
    state.current_organization_plan_active = bool
  },
  SET_EMPTY_STATE: (state, emptyState) => {
    state.emptyState = emptyState
  },
  SET_CURRENT_PROJECT: (state, payload) => {
    state.currentProject = payload
  },
  SET_NOT_ALLOWED_MODAL_SHOWN: (state, payload) => {
    state.notAllowedModalShown = payload
  },
  EMPTY_CURRENT_PROJECT: (state) => {
    state.currentProject = null
  },
  SET_IS_RETRIEVING: (state, isRetrieving) => {
    state.isRetrieving = isRetrieving
  },
  INCREMENT_PROJECT_WIZARD_STEP: (state, steps) => {
    if (state.projectWizard.step < 3) {
      state.projectWizard.step += steps
    }
  },
  SET_PROJECT_WIZARD_STEP: (state, step) => {
    if (step > 4) {
      throw new Error('Step length out of bounds')
    }
    state.projectWizard.step = step
  },
  PROJECT_WIZARD_CUSTOM_BUSINESS_KIT: (state, bool) => {
    state.projectWizard.customBusinessKit = bool
  },
  ADD_PROJECT_WIZARD_ERROR: (state, error) => {
    // Don't add if an error is already there for the field
    if (!state.projectWizard.errors.some(e => e.field_name === error.field_name)) {
      state.projectWizard.errors.push(error)
    }
  },
  RESET_PROJECT_WIZARD_ERRORS: (state) => {
    state.projectWizard.errors = []
  },
  SET_NOTIFICATION: (state, notification) => {
    notification.visible = true
    notification.id      = generateUuidv4()
    if (!notification.type || !['info', 'error', 'success'].includes(notification.type)) {
      notification.type = 'info'
    }
    if (!notification.classes) {
      notification.classes = ['animateOpen']
    }
    if (!notification.duration_ms && notification.duration_ms !== 0) {
      notification.duration_ms = 50 * notification.title.length
      // Set minimum
      if (notification.duration_ms < 4500) {
        notification.duration_ms = 4500
      } else if (notification.duration_ms > 7500) {
        notification.duration_ms = 7500
      }
    }
    if (state.notifications.length <= state.maxNotificationsCount) {
      state.notifications.push(notification)
    }
  },
  SET_NOTIFICATION_CLOSED: (state, id) => {
    let notificationIndex = state.notifications.indexOf(state.notifications.find(notification => {
      return notification.id === id
    }))
    // Can be undefined if notifications are emptied
    if (notificationIndex !== undefined) {
      state.notifications.splice(notificationIndex,1)
    }
    state.notifications.forEach((notification) => {
      notification.classes.splice(notification.classes.indexOf('animateOpen',1))
    })
  },
  EMPTY_NOTIFICATION: (state) => {
    state.notifications = []
  },
  SET_PAGE_TITLE: (state, title) => {
    state.page_title = title
  },
  SET_CURRENT_PAGE: (state, page_number) => {
    if (state.page_type == 'project') {
      state.currentPage.projects = parseInt(page_number)
    } else if (state.page_type == 'project_lead') {
      state.currentPage.leads = parseInt(page_number)
    } else if (state.page_type == 'user') {
      state.currentPage.users = parseInt(page_number)
    } else if (state.page_type == 'external_form') {
      state.currentPage.externalForms = parseInt(page_number)
    } else if (state.page_type == 'organization') {
      state.currentPage.organizations = parseInt(page_number)
    } else if (state.page_type == 'lead_mask') {
      state.currentPage.leadMasks = parseInt(page_number)
    } else if (state.page_type == 'email_template') {
      state.currentPage.emailTemplates = parseInt(page_number)
    }
  },
  RESET_CURRENT_PAGE_PROJECTS: (state) => {
    state.currentPage.projects = 1
  },
  RESET_CURRENT_PAGE_LEADS: (state) => {
    state.currentPage.leads = 1
  },
  RESET_CURRENT_PAGE_LEAD_MASKS: (state) => {
    state.currentPage.leadMasks = 1
  },
  RESET_CURRENT_PAGE_USERS: (state) => {
    state.currentPage.users = 1
  },
  RESET_CURRENT_PAGE_EXTERNAL_FORMS: (state) => {
    state.currentPage.externalForms = 1
  },
  RESET_CURRENT_PAGE_ORGANIZATIONS: (state) => {
    state.currentPage.organizations = 1
  },
  RESET_CURRENT_PAGE_EMAIL_TEMPLATES: (state) => {
    state.currentPage.emailTemplates = 1
  },
  RESET_CURRENT_PAGE_WORKFLOWS: (state) => {
    state.currentPage.workflows = 1
  },
  RESET_ALL_PAGES: (state) => {
    state.currentPage.organizations = 1
    state.currentPage.users = 1
    state.currentPage.projects = 1
    state.currentPage.leads = 1
    state.currentPage.emailTemplates = 1
    state.currentPage.externalForms = 1
    state.currentPage.leadMasks = 1
    state.currentPage.workflows = 1
  },
  SET_PAGE_TITLE_NAME: (state, lead) => {
    let string = ''
    lead.firstname ? string += lead.firstname : string
    string += ' '
    lead.lastname ? string += lead.lastname : string
    string = string.trim()
    if (string)  {
      state.page_title = { label: string }
    }else{
      state.page_title = { label: lead.email }
    }
  },
  SET_PAGE_BREADCRUMBS: (state, breadcrumbs) => {
    state.page_breadcrumbs = breadcrumbs
  },
  SET_PAGE_SUBTITLE: (state, title) => {
    state.parent_page_title = title
  },
  SET_PAGE_LEVEL: (state, level) => {
    state.page_level = level
  },
  SET_PAGE_TYPE: (state, type) => {
    state.page_type = type
  },
  SET_HEADER_BUTTONS: (state, buttons) => {
    state.header_buttons = buttons
  },
  ADD_ERROR: (state, error) => {
    state.error = error
  },
  EMPTY_ERROR: (state) => {
    state.error = ""
  },
  EMPTY_FOLDED_NAVIGATION_HIDDEN_ITEMS: (state) => {
    state.folded_navigation_hidden_items = []
  },
  ADD_FOLDED_NAVIGATION_HIDDEN_ITEMS: (state, item) => {
    state.folded_navigation_hidden_items.push(item)
  },
  SET_DRAWER_VISIBLE: (state, boolean) => {
    state.drawer_visible = boolean
  },
  SET_CURRENT_USER: (state, user) => {
    if (user.overwrite) {
      state.current_user = user
    } else if (state.current_user === null) {
      state.current_user = user
    }
  },
  SET_CURRENT_USER_FIRSTNAME: (state, firstName) => {
    state.current_user.firstname = firstName
  },
  SET_CURRENT_USER_LASTNAME: (state, lastName) => {
    state.current_user.lastname = lastName
  },
  SET_CURRENT_USER_ONBOARDED: (state) => {
    state.current_user.is_onboarded = true
  },
  SET_CURRENT_USER_HAS_SEEN_SCREEN: (state) => {
    state.app_download_screen = true
  },
  EMPTY_CURRENT_USER: (state) => {
    state.current_user = null
  },
  SET_TABS: (state, tabs) => {
    state.tabs = tabs
  },
  SET_TABS_IN_EDITOR: (state, tabs) => {
    state.tabs = tabs
  },
  EMPTY_TABS: (state) => {
    state.tabs = []
  },
  SET_ACTIVE_TAB: (state, name) => {
    state.active_tab = name
  },
  NEXT_TAB: (state) => {
    let newTab
    state.tabs.forEach((tab, key) => {
      if (tab.name == state.active_tab) {
        let newKey = key + 1
        if (state.tabs[newKey]) {
          newTab = state.tabs[newKey].name
        }
      }
    })
    if (newTab) {
      state.active_tab = newTab
    }
  },
  PREV_TAB: (state) => {
    let newTab
    state.tabs.forEach((tab, key) => {
      if (tab.name == state.active_tab) {
        let newKey = key - 1
        if (state.tabs[newKey]) {
          newTab = state.tabs[newKey].name
        }
      }
    })
    if (newTab) {
      state.active_tab = newTab
    }
  },
  SET_INTERCOM_INITIALIZED: (state, bool) => {
    state.intercomInitialized = bool
  },
}

const actions = {
  addAxiosError(context, error_message, type = 'error') {
    context.commit('SET_NOTIFICATION', {
      'title': error_message,
      'type': type
    })
  },
  redirectTo(context, route) {
    router.push(route)
  },
  routerGo(context, steps) {
    router.go(steps)
    // clear route
  }
}

const getters = {
  getHeaderState(state) {
    return state.header
  },
  getDragEditorActiveTab(state) {
    return state.dragEditor.activeTab
  },
  getDragEditorSubmitButton() {
    return state.dragEditor.submitButton
  },
  getDragEditorCurrentStructure() {
    return state.dragEditor.currentStructure
  },
  getDragEditorActiveItem(state) {
    return state.dragEditor.activeItem
  },
  getDragEditorUnpublishedChanges(state) {
    return state.dragEditor.unpublishedChanges
  },
  getDragEditorEdited(state) {
    return state.dragEditor.edited
  },
  getErrors(state) {
    return state.errors
  },
  getMainContainerClasses(state) {
    return state.mainContainerClasses
  },
  getHeaderButtons(state) {
    return state.header_buttons
  },
  getNotifications(state) {
    return state.notifications
  },
  currentUser(state) {
    return state.current_user
  },
  getCurrentProject(state) {
    return state.currentProject
  },
  getCurrentUserIsAdmin(state) {
    if (!state.current_user) {
      return false
    }
    let role = state.current_user.role
    return role === 'admin' || role === 'root'
  },
  getCurrentUserIsRoot(state) {
    if (!state.current_user) {
      return false
    }
    let role = state.current_user.role
    return role === 'root'
  },
  getTitle(state) {
    return state.page_title
  },
  getEmptyState(state) {
    return state.emptyState
  },
  getProjectWizardStep(state) {
    return state.projectWizard.step
  },
  getProjectWizardErrors(state) {
    return state.projectWizard.errors
  },
  getProjectWizardCustomBusinessKit(state) {
    return state.projectWizard.customBusinessKit
  },
  getIsRetrieving(state) {
    return state.isRetrieving
  },
  getBreadcrumbs(state) {
    return state.page_breadcrumbs
  },
  getSubtitle(state) {
    return state.parent_page_title
  },
  getOrganizationConfigs(state) {
    return state.organization_config
  },
  getOrganizationConfig: (state) => (config_name) => {
    let prop = 'organization_config.' + config_name + '.content'
    return lodash.get(state, prop)
  },
  getCurrentPageProjects(state) {
    return state.currentPage.projects
  },
  getCurrentPageSegments(state) {
    return state.currentPage.segments
  },
  getCurrentPageLeads(state) {
    return state.currentPage.leads
  },
  getCurrentLead(state) {
    return state.lead
  },
  getCurrentPageLeadMasks(state) {
    return state.currentPage.leadMasks
  },
  getCurrentPageUsers(state) {
    return state.currentPage.users
  },
  getCurrentPageOrganizations(state) {
    return state.currentPage.organizations
  },
  getCurrentPageExternalForms(state) {
    return state.currentPage.externalForms
  },
  getCurrentPageEmailTemplates(state) {
    return state.currentPage.emailTemplates
  },
  getCurrentPageWorkflows(state) {
    return state.currentPage.workflows
  },
  getCurrentOrganizationPlanActive(state) {
    return state.current_organization_plan_active
  },
  getCurrentOrganizationPlan(state) {
    if (!state.current_user || !state.current_user.organization) {
      return null
    }
    return state.current_user.organization.plan
  },
  getCurrentPlanIsTalent(state) {
    if (!state.current_user || !state.current_user.organization) {
      // If the plan is not set (fx on login page) use the URL as a fallback
      return window.location.hostname.includes('talent')
    }
    return state.current_user.organization.plan.name === 'TALENT'
  },
  getNotAllowedModalShown(state) {
    return state.notAllowedModalShown
  },
  getIntercomSettings(state) {
    if (!state.current_user || !state.current_user.organization) {
      return null
    }
    return {
      "role": state.current_user.role,
      "user_id": state.current_user.id,
      "email": state.current_user.email,
      "name": state.current_user.display_name,
      "created_at": state.current_user.created_at,
      "plan_name": state.current_user.organization.plan.name,
      "app_id": "qsyfi6lp",
      "project_count": state.current_user.organization.project_count,
      "lead_count": state.current_user.organization.lead_count,
      "company": {
        "id":  state.current_user.organization.id,
        "name": state.current_user.organization.name,
        "created_at": state.current_user.organization.created_at,
        "eventbaxx_event_count": state.current_user.organization.eventbaxx_event_count,
      }
    }
  },
  getIntercomInitialized(state) {
    return state.intercomInitialized
  },
  getUserIsOnboarded(state) {
    if (state.current_user && 'is_onboarded' in state.current_user) {
      return state.current_user.is_onboarded
    }
    return true
  },
  getAppDownloadScreen(state) {
    return state.getAppDownloadScreen
  },
  getSecondaryHeaderTabs(state) {
    return state.secondaryHeaderTabs
  },
  getActiveTab(state) {
    return state.active_tab
  },
  getCurrentLanguage(state) {
    return state.language
  },
  getBusinessKitLink(state, getters) {
    let config    = state.organization_config.eventbaxx_auth_token
    let url       = undefined
    let isStaging = window.location.hostname.includes('staging')

    let eventbaxxUrl = process.env.EVENTBAXX_URL

    if (eventbaxxUrl) {
      url = `${eventbaxxUrl}/auth_login/`
    } else if (isStaging) {
      url = 'https://events-staging.fanomena.io/auth_login/'
    } else {
      let talent = getters.getCurrentPlanIsTalent
      if (talent) {
        url = 'https://talentkit.fanomena.io/auth_login/'
      } else {
        url = 'https://businesskit.fanomena.io/auth_login/'
      }
    }
    // Append eventbaxx token for automatic login
    if (config) {
      url += config.content
    }
    return url
  },
  getFavIcoAndTitle(state, getters) {
    let talent = getters.getCurrentPlanIsTalent
    if (talent) {
      return {
        ico: "/static/talents_logo.ico",
        title: "Talent"
      }
    } else {
      return {
        ico: "/static/logo_sign.ico",
        title: "Leads"
      }
    }
  },
  getFooterLinks(state, getters) {
    let talent = getters.getCurrentPlanIsTalent
    if (talent) {
      return {
        'imprint': 'https://fanomena.io/impressum/',
        'privacy': 'https://fanomena.io/datenschutz/',
        'terms': '#',
      }
    } else {
      return {
        'imprint': 'https://fanomena.io/impressum/',
        'privacy': 'https://fanomena.io/datenschutz/',
        'terms': 'https://fanomena.io/impressum/',
      }
    }
  },
  getFooterDisplay(state) {
    return state.footer.display
  },
  getHileadzzPublicFormUrl(state) {
    // Return JS for displaying hileadzz forms
    // Return an overwriteUrl as well so we can target a specific API
    if (process.env.NODE_ENV === "development") {
      return {
        js: 'http://localhost:4567/js/hileadzz_form.js',
        apiOverwrite: 'http://localhost:4567'
      }
    } else {
      // In this case the apiOverwrite is so that our staging server, which runs in production,
      // can overwrite the URL and use the staging API
      return {
        js: `${process.env.FORM_URL}/js/hileadzz_form-min.js`,
        apiOverwrite: process.env.FORM_URL
      }
    }
  },
}

let store = new Vuex.Store({
  state,
  actions,
  mutations,
  getters,
  strict: process.env.NODE_ENV !== 'production'
})

// global.store = store

export default store



// WEBPACK FOOTER //
// ./src/store.js